/**
 * Patient email body builder — the ONE place staff-typed text becomes the HTML that
 * teamio-utility's generic email chute sends verbatim (utility.js sendEmailWithSendGrid, the
 * subject/html branch: `content: [{ type: 'text/html', value: msgData.emailHtml }]`).
 *
 * WHY THIS EXISTS (2026-09-10)
 *   PatientSidebar's Send Form email passed the textarea text straight into HTML:
 *       emailHtml: this.emailMessage + '<br><br>' + formLinks
 *   HTML collapses newlines to spaces, so every practice's forms email arrived as one run-on line
 *   ("Hello Jane, Please fill out the following forms: - New Patient Registration Thank you, Allay
 *   Dental Group"). Rendered and confirmed 2026-09-10. The same line sent whatever markup a staff
 *   member typed, unescaped. The new patient Email compose panel shares this builder.
 *
 * Keep this file IMPORT-FREE: cypress/node-tests/email-body.test.js strips the `export` keywords
 * and executes this exact file (same shim as sms-template-text.test.js), so the test binds to the
 * shipped code rather than a mirror.
 *
 * Backend contract (teamio-utility utility.js, generic branch):
 *   - replaceTemplateVariables() substitutes {{tokens}} and processLink() turns
 *     <a href="{{patient_link_for_form_<uuid>}}">…</a> into a real patient URL — which is why
 *     the [Form: …] placeholders the composer shows must go back to their anchors here.
 *   - stripEmailSignatureTokens() removes {{email_signature_block}} when the practice has no
 *     signature configured (Settings → Email), so appending the token is always safe.
 */

export const EMAIL_SIGNATURE_TOKEN = '{{email_signature_block}}';

// Readable default in every mail client; the practice signature block carries its own styling.
const BODY_STYLE = 'font-family: Arial, Helvetica, sans-serif; font-size: 15px; line-height: 1.5; color: #1f2937;';

export function escapeHtml(text) {
  return String(text == null ? '' : text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Put the [Form: …] / [Packet: …] / [Link: …] placeholders that PatientSidebar.parseEditorJsContent
 * showed the user back as the ORIGINAL anchors, so the backend's processLink can resolve them.
 * Runs on text that is already ESCAPED and newline-converted, so each placeholder key is escaped
 * AND newline-normalised (\r\n|\r|\n → <br>) the same way as the text before matching — otherwise a
 * key that happened to span lines could never match ("[Form: Brush & Floss]" is
 * "[Form: Brush &amp; Floss]" by then). Every occurrence is replaced;
 * a placeholder the user deleted simply produces no link. Keys are processed LONGEST FIRST so a key
 * that is a prefix of another (e.g. "[Form: A]" vs "[Form: A]B") can never consume the other's match.
 */
export function restoreLinkPlaceholders(escapedText, linkMap) {
  let out = String(escapedText == null ? '' : escapedText);
  if (!linkMap || typeof linkMap !== 'object') return out;
  for (const placeholder of Object.keys(linkMap).sort((a, b) => b.length - a.length)) {
    const anchor = linkMap[placeholder];
    if (typeof anchor !== 'string' || !anchor) continue;
    out = out.split(escapeHtml(placeholder).replace(/\r\n|\r|\n/g, '<br>')).join(anchor);
  }
  return out;
}

/**
 * Escape → newline to <br> → restore anchors. Newlines convert BEFORE anchors are restored, so a
 * `<br>` can never be injected inside an anchor tag even if a linkMap value spans lines; the
 * placeholder keys are single-line by construction, so nothing is lost. Braces are not HTML, so
 * {{variables}} survive.
 */
export function plainTextToEmailHtml(text, opts) {
  const o = opts || {};
  const escaped = escapeHtml(text);
  const withBreaks = escaped.replace(/\r\n|\r|\n/g, '<br>');
  return restoreLinkPlaceholders(withBreaks, o.linkMap);
}

/**
 * The full body for one patient email.
 *   message          staff-typed text (textarea)
 *   linkMap          placeholders → anchors (from parseEditorJsContent), optional
 *   appendHtml       already-HTML block placed after the message (Send Form's form links), optional
 *   includeSignature default true — appends {{email_signature_block}}
 *
 * Trust contract: `linkMap` anchor values and `appendHtml` are inserted as trusted HTML and must
 * never carry raw staff-typed text (only `message` is escaped here).
 */
export function buildPatientEmailHtml(input) {
  const i = input || {};
  const parts = [plainTextToEmailHtml(i.message, { linkMap: i.linkMap })];
  if (typeof i.appendHtml === 'string' && i.appendHtml) parts.push(i.appendHtml);
  if (i.includeSignature !== false) parts.push(EMAIL_SIGNATURE_TOKEN);
  return `<div style="${BODY_STYLE}">${parts.join('<br><br>')}</div>`;
}

/**
 * The Send Form email picker rows → one { placeholder, anchor } per row, so the form NAME itself is
 * the clickable link on its list line (not a name in the text plus a separate link block below).
 *   forms  the picker rows: { Name, FormIDForWeb, _isPacket, PacketID }
 * Placeholder is `[Form: <name>]` / `[Packet: <name>]` (a blank name falls back to the kind word);
 * the 2nd, 3rd… row with the same kind+name gets ` (2)`, ` (3)` in the PLACEHOLDER only, so each
 * placeholder maps to exactly one link — the anchor text stays the plain name. Anchor href is the
 * {{patient_link_for_form_<id>}} / {{patient_link_for_packet_<id>}} token the backend's processLink
 * resolves; the name is escaped (practice-authored, but still text). A row missing its id is skipped;
 * a non-array input returns [].
 */
export function formLinkPlaceholders(forms) {
  if (!Array.isArray(forms)) return [];
  const entries = [];
  const counts = Object.create(null);
  for (const form of forms) {
    if (!form) continue;
    const isPacket = !!form._isPacket;
    const kind = isPacket ? 'Packet' : 'Form';
    const id = isPacket ? form.PacketID : form.FormIDForWeb;
    if (!id) continue;
    const name = String(form.Name == null ? '' : form.Name).trim() || kind;
    const key = kind + ':' + name;
    counts[key] = (counts[key] || 0) + 1;
    const suffix = counts[key] > 1 ? ` (${counts[key]})` : '';
    const token = isPacket ? `{{patient_link_for_packet_${id}}}` : `{{patient_link_for_form_${id}}}`;
    entries.push({
      placeholder: `[${kind}: ${name}${suffix}]`,
      anchor: `<a href="${token}">${escapeHtml(name)}</a>`,
    });
  }
  return entries;
}

/**
 * formLinkPlaceholders() entries → { placeholder: anchor } for buildPatientEmailHtml's linkMap.
 */
export function linkMapFromPlaceholders(entries) {
  const map = {};
  for (const e of (Array.isArray(entries) ? entries : [])) {
    if (e && typeof e.placeholder === 'string') map[e.placeholder] = e.anchor;
  }
  return map;
}

/**
 * The safety net: `<p>anchor</p>` for every entry whose placeholder is NOT in the message verbatim
 * (joined in entry order), else ''. A forms email must never go out without a form's link, even if a
 * staff member deleted or edited the placeholder out of the textarea — the link is appended instead.
 */
export function appendMissingFormLinks(message, entries) {
  const msg = String(message == null ? '' : message);
  const missing = [];
  for (const e of (Array.isArray(entries) ? entries : [])) {
    if (!e || typeof e.placeholder !== 'string') continue;
    if (msg.indexOf(e.placeholder) === -1) missing.push('<p>' + e.anchor + '</p>');
  }
  return missing.join('');
}

/**
 * Only a page whose save actually baked a SendGrid dynamic template can be sent by id. The
 * `email-template` TAG alone is not enough (an author can tag without ticking the checkbox), and
 * SMS-only pages store the literal string 'undefined' — which utility.js already treats as a
 * misconfiguration and skips with a page.
 */
export function isSendgridBackedEmailTemplate(row) {
  if (!row || Number(row.IsEmailTemplate) !== 1) return false;
  const id = typeof row.SendgridTemplateID === 'string' ? row.SendgridTemplateID.trim() : '';
  return id.startsWith('d-');
}

/**
 * The Email panel's template list: designed email templates (sent by id, unchanged path) first,
 * then SMS templates (copied into the compose box). A page flagged as BOTH appears once, as Email —
 * the designed version is what the author intended patients to receive by email.
 */
export function mergeEmailPanelTemplates(emailRows, smsRows) {
  const byId = new Map();
  for (const row of (Array.isArray(emailRows) ? emailRows : [])) {
    if (!row || !row.WikiPageID || !isSendgridBackedEmailTemplate(row)) continue;
    byId.set(row.WikiPageID, Object.assign({}, row, { Kind: 'Email' }));
  }
  for (const row of (Array.isArray(smsRows) ? smsRows : [])) {
    if (!row || !row.WikiPageID || byId.has(row.WikiPageID)) continue;
    if (Number(row.IsSmsTemplate) !== 1) continue;
    byId.set(row.WikiPageID, Object.assign({}, row, { Kind: 'Text' }));
  }
  return Array.from(byId.values()).sort((a, b) => {
    if (a.Kind !== b.Kind) return a.Kind === 'Email' ? -1 : 1;
    return String(a.Name || '').localeCompare(String(b.Name || ''));
  });
}

/**
 * Why a composed email must NOT be sent — every reason, so the operator fixes them all at once.
 * The unknown-variable sentence is passed in (templateVariables.js describes it) to keep this file
 * import-free. A greeting-only message is a blank message: the textarea is prefilled with
 * `Hello <FName>,\n\n` (emailGreeting), so when a non-empty `greeting` is passed and the trimmed
 * message equals the trimmed greeting, refuse with the SAME sentence as a blank message — clicking
 * Send without typing must not send just "Hello Jane,". Credits: refused only when the wallet is ON
 * and a LOADED, numeric balance is ≤ 0. The balance is coerced with Number() so a string "0" still
 * refuses; but null, undefined, a blank or whitespace-only string, and any non-numeric value are
 * treated as NOT LOADED and fail open (a whitespace-only string must not become Number("   ") === 0)
 * — the backend engine is the authority and blocks on its own.
 */
export function emailComposeProblems(input) {
  const i = input || {};
  const problems = [];
  if (!String(i.subject == null ? '' : i.subject).trim()) problems.push('Please enter a subject line before sending.');
  const message = String(i.message == null ? '' : i.message);
  const greeting = typeof i.greeting === 'string' ? i.greeting.trim() : '';
  const greetingOnly = greeting !== '' && message.trim() === greeting;
  if (!message.trim() || greetingOnly) problems.push('Please enter an email message before sending.');
  if (Array.isArray(i.unknownVariables) && i.unknownVariables.length > 0) {
    problems.push(i.unknownVariablesMessage || 'This message uses a variable we cannot fill in. Pick one from the + Insert menu, or delete it.');
  }
  const raw = i.creditsAvailable;
  const credits = (raw === null || raw === undefined || String(raw).trim() === '') ? NaN : Number(raw);
  if (i.walletOn === true && Number.isFinite(credits) && credits <= 0) {
    problems.push('Your practice is out of email credits. Add credits from the balance in the header, then try again.');
  }
  return problems;
}

export default {
  EMAIL_SIGNATURE_TOKEN,
  escapeHtml,
  restoreLinkPlaceholders,
  plainTextToEmailHtml,
  buildPatientEmailHtml,
  isSendgridBackedEmailTemplate,
  mergeEmailPanelTemplates,
  emailComposeProblems,
  formLinkPlaceholders,
  linkMapFromPlaceholders,
  appendMissingFormLinks,
};
